/**
 * Application-wide cache instances for different data types
 * Uses the CacheFactory to create and manage caches with appropriate TTLs
 */

import cacheFactory from './CacheFactory';
import * as LanternApi from './LanternApiClient';

// Cache TTL configurations (in milliseconds)
const CACHE_TTLS = {
  RETAILERS: 5 * 60 * 1000, // 5 minutes - retailers change infrequently
  CLIENTS: 2 * 60 * 1000, // 2 minutes - clients change more often
  USER_DATA: 10 * 60 * 1000, // 10 minutes - user info changes rarely
  GOALS_CLIENTS: 5 * 60 * 1000, // 5 minutes - NetSuite clients change infrequently
  INTERNAL_USERS: 15 * 60 * 1000, // 15 minutes - internal users change rarely
};

// Create cache instances
export const retailerCache = cacheFactory.getCache('retailers', CACHE_TTLS.RETAILERS);
export const clientCache = cacheFactory.getCache('clients', CACHE_TTLS.CLIENTS);
export const userDataCache = cacheFactory.getCache('userData', CACHE_TTLS.USER_DATA);
export const goalsClientCache = cacheFactory.getCache('goalsClients', CACHE_TTLS.GOALS_CLIENTS);
export const internalUserCache = cacheFactory.getCache('internalUsers', CACHE_TTLS.INTERNAL_USERS);

// Retailer API wrapper functions
export const getRetailers = (params = {}) => {
  const fetchFunction = async (params) => {
    const {
      shouldFilterByClientId = false,
      parentClientId = 0,
      shouldAuthorize = true,
      showAllOption = false,
      retailersToFilterOut = null,
    } = params;

    let response;

    if (shouldFilterByClientId) {
      response = await LanternApi.getRetailersByParentClient(parentClientId);
    } else {
      response = shouldAuthorize ? await LanternApi.getRetailers() : await LanternApi.getAllRetailersWithoutAuthorization();
    }

    if (!response.success) {
      throw new Error('Failed to fetch retailers');
    }

    let retailers = response.body.records || response.body;

    // Sort by name
    retailers = retailers.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

    // Add "All Retailers" option if requested
    if (showAllOption) {
      const allRetailers = {
        name: 'All Retailers',
        id: 0,
        retailPartner: 'AllRetailers',
      };
      retailers.unshift(allRetailers);
    }

    // Filter out specific retailers if requested
    if (retailersToFilterOut && Array.isArray(retailersToFilterOut)) {
      retailers = retailers.filter(
        (retailer) => !retailersToFilterOut.some((retailerName) => retailer.name.toLowerCase() === retailerName.toLowerCase()),
      );
    }

    return retailers;
  };

  return retailerCache.get(fetchFunction, params);
};

// Client API wrapper functions
export const getParentClients = (params = {}) => {
  const fetchFunction = async () => {
    const response = await LanternApi.getParentClients();
    if (!response.success) {
      throw new Error('Failed to fetch parent clients');
    }
    return response.body.records || response.body;
  };

  return clientCache.get(fetchFunction, params);
};

export const getChildClients = (params = {}) => {
  const fetchFunction = async (params) => {
    const response = await LanternApi.getChildClients(params);
    if (!response.success) {
      throw new Error('Failed to fetch child clients');
    }
    return response.body.records || response.body;
  };

  return clientCache.get(fetchFunction, params);
};

// Goals clients API wrapper
export const getClientGoalsClients = (params = {}) => {
  const fetchFunction = async () => {
    const response = await LanternApi.getClientGoalsClientsList();
    if (!response.success) {
      throw new Error('Failed to fetch client goals clients');
    }
    const clients = response.body;
    clients.sort((a, b) => a.clientName.localeCompare(b.clientName));
    return clients;
  };

  return goalsClientCache.get(fetchFunction, params);
};

// Internal users API wrapper
export const getInternalUsersByChildClient = (params = {}) => {
  const fetchFunction = async (params) => {
    const { childClientId } = params;
    const response = await LanternApi.getInternalUsersByChildClient(childClientId);
    if (!response.success) {
      throw new Error('Failed to fetch internal users');
    }
    return response.body;
  };

  return internalUserCache.get(fetchFunction, params);
};

// User data API wrappers
export const getUserSettings = (params = {}) => {
  const fetchFunction = async () => {
    const response = await LanternApi.getUserSettings();
    if (!response.success) {
      throw new Error('Failed to fetch user settings');
    }
    return response.body;
  };

  return userDataCache.get(fetchFunction, params);
};

export const getCurrentUser = (params = {}) => {
  const fetchFunction = async () => {
    // Placeholder for Microsoft Graph /me endpoint
    // This would typically use MSAL to get user info
    throw new Error('getCurrentUser not yet implemented');
  };

  return userDataCache.get(fetchFunction, params);
};

export const getUserMemberships = (params = {}) => {
  const fetchFunction = async () => {
    // Placeholder for Microsoft Graph /memberOf endpoint
    // This would typically use MSAL to get user group memberships
    throw new Error('getUserMemberships not yet implemented');
  };

  return userDataCache.get(fetchFunction, params);
};

// Utility function to clear all caches (for sign out)
export const clearAllCaches = () => {
  cacheFactory.clearAllCaches();
};

// Get cache statistics (for debugging)
export const getCacheStats = () => {
  return cacheFactory.getAllStats();
};

export default {
  // Cache instances
  retailerCache,
  clientCache,
  userDataCache,
  goalsClientCache,
  internalUserCache,

  // API wrapper functions
  getRetailers,
  getParentClients,
  getChildClients,
  getClientGoalsClients,
  getInternalUsersByChildClient,
  getUserSettings,
  getCurrentUser,
  getUserMemberships,

  // Utilities
  clearAllCaches,
  getCacheStats,
};
